"use client";

import { useCallback, useRef } from "react";
import { openEtfOverlapGuideSheet } from "../components/openEtfOverlapGuideSheet";
import type { OverlapEntryContext } from "../types";
import { setPendingOverlapContext } from "../utils/overlapChatContext";
import { useOverlapConnectFlows } from "./useOverlapConnectFlows";
import {
  type OverlapRouteOptions,
  type OverlapRouteResult,
  useOverlapEntryRouter,
} from "./useOverlapEntryRouter";

export interface UseOverlapEntryOptions {
  returnToBrief?: boolean;
}

/**
 * 重叠分析的 App 内统一入口（Brief 卡 / Invest 入口卡 / Ask Ed 推荐问题共用，EDG-561 FD-1）：
 * 有持仓 → 直接进 Chat 发协议行；无持仓 / 探测失败 → 引导弹窗（SC-04），按选择连券商或传截图。
 * 弹窗被 scrim / 下滑 / Esc 关闭 → 什么都不做，用户回到发起页原位。
 */
export const useOverlapEntry = ({
  returnToBrief,
}: UseOverlapEntryOptions = {}) => {
  const { connectBroker, openUpload } = useOverlapConnectFlows();
  // 弹窗打开期间再次点入口：opener 会返回同一个 promise，没有这个守卫两处 await 会各跑一遍 connect / upload
  const guideOpenRef = useRef(false);

  const onNoHoldings = useCallback(
    async (ctx: OverlapEntryContext): Promise<void> => {
      if (guideOpenRef.current) {
        return;
      }
      guideOpenRef.current = true;
      try {
        const action = await openEtfOverlapGuideSheet(ctx);
        if (action) {
          // 只在用户真的选了连接 / 上传时记入口；关闭弹窗什么都不留，避免后续结果沿用旧入口
          setPendingOverlapContext({ entry_source: ctx.entry_source });
        }
        if (action === "connect") {
          await connectBroker({
            trackingEntry: "overlap_guide",
            returnToBrief,
          });
        } else if (action === "upload") {
          await openUpload("guide");
        }
      } finally {
        guideOpenRef.current = false;
      }
    },
    [connectBroker, openUpload, returnToBrief],
  );

  const { routeToOverlap, isRouting } = useOverlapEntryRouter({
    onNoHoldings: (ctx) => void onNoHoldings(ctx),
    returnToBrief,
  });

  /**
   * 返回分流结果（`chat` = 有持仓直达 Chat、`guide` = 无持仓开了引导、`ignored` = 探测进行中的重复点击），
   * Brief 卡据此给 `overlap_entry_tap.has_holdings` 取值。
   */
  const enter = useCallback(
    (
      ctx: OverlapEntryContext,
      options?: OverlapRouteOptions,
    ): Promise<OverlapRouteResult> =>
      options ? routeToOverlap(ctx, options) : routeToOverlap(ctx),
    [routeToOverlap],
  );

  return { enter, isRouting };
};
